import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Notice } from '@/components/ui/notice';
import { type LoadError, apiErrorMessage, toLoadError, loadErrorText } from '@/lib/api-error';
import { listChildMeetings, rsvpToMeeting } from '@/features/meetings/api/meetings-api';
import { formatMeetingWhen } from '@/features/meetings/lib/meeting-time';
import { RsvpButtonGroup } from '@/features/meetings/components/rsvp-button-group';
import type { InviteStatus, MeetingDto } from '@/features/meetings/types';
import { inviteStatusLabel } from '@/lib/invite-status-label';

function nextUpcoming(meetings: MeetingDto[]): MeetingDto | null {
  const now = Date.now();
  return (
    meetings
      .filter(
        (m) => (m.status === 'Scheduled' || m.status === 'Proposed') && new Date(m.startsAtUtc).getTime() >= now
      )
      .sort((a, b) => new Date(a.startsAtUtc).getTime() - new Date(b.startsAtUtc).getTime())[0] ?? null
  );
}

const inviteBadgeVariant: Record<InviteStatus, 'success' | 'error' | 'warning' | 'neutral'> = {
  Accepted: 'success',
  Declined: 'error',
  Tentative: 'warning',
  Pending: 'neutral',
};

/** Parent child-overview card: the child's next scheduled meeting with
 * Accept/Decline/Tentative RSVP buttons. Renders nothing if there is none. */
export function UpcomingMeetingCard({ childId }: { childId: number }) {
  const { t } = useTranslation(['children', 'common']);
  // `undefined` = loading, `null` = loaded with nothing upcoming.
  const [meeting, setMeeting] = useState<MeetingDto | null | undefined>(undefined);
  const [loadError, setLoadError] = useState<LoadError | null>(null);
  // A failed RSVP is shown inline under the buttons; the card (and the meeting)
  // stays visible so the family can simply try the response again.
  const [rsvpError, setRsvpError] = useState<string | null>(null);
  const [responding, setResponding] = useState<InviteStatus | null>(null);
  // Bumped by the "Try again" button to re-run the load effect below.
  const [retryToken, setRetryToken] = useState(0);
  // The overview route keeps this card mounted across a `:childId` change (a
  // bell link from one child to another), so per-child RSVP state is reset
  // during render when the child changes — same idiom as meeting-drawer.tsx.
  const [seenChildId, setSeenChildId] = useState(childId);
  if (childId !== seenChildId) {
    setSeenChildId(childId);
    setRsvpError(null);
  }

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await listChildMeetings(childId);
        if (!active) return;
        if (response.success && response.data) {
          setMeeting(nextUpcoming(response.data));
          setLoadError(null);
        } else {
          setLoadError(toLoadError(response));
        }
      } catch (err) {
        if (!active) return;
        setLoadError(toLoadError(err));
      }
    })();
    return () => {
      active = false;
    };
    // `t` deliberately excluded — see the `LoadError` comment above.
  }, [childId, retryToken]);

  const error = loadErrorText(loadError, t('upcomingMeeting.loadError'));

  const handleRsvp = async (status: InviteStatus) => {
    if (!meeting) return;
    setResponding(status);
    setRsvpError(null);
    try {
      const response = await rsvpToMeeting(meeting.id, { status });
      if (response.success && response.data) setMeeting(response.data);
      else setRsvpError(response.message ?? t('upcomingMeeting.rsvpError'));
    } catch (err) {
      setRsvpError(apiErrorMessage(err, t('upcomingMeeting.rsvpError')));
    } finally {
      setResponding(null);
    }
  };

  if (error) {
    return (
      <Card data-testid="upcoming-meeting-error">
        <div role="alert">
          <Notice variant="error" title={error}>
            <Button size="sm" variant="secondary" onClick={() => setRetryToken((n) => n + 1)}>
              {t('common:ui.tryAgain')}
            </Button>
          </Notice>
        </div>
      </Card>
    );
  }

  if (meeting === undefined || meeting === null) return null;

  return (
    <Card data-testid="upcoming-meeting-card">
      <div className="mb-2 flex items-center gap-2">
        <Calendar className="h-4 w-4 text-brand-teal-500" strokeWidth={1.8} aria-hidden="true" />
        <h2 className="font-serif text-base text-brand-slate-800">{t('upcomingMeeting.heading')}</h2>
      </div>
      <p className="font-medium text-brand-slate-800">{meeting.title || meeting.type}</p>
      <p className="text-sm text-brand-slate-600">{formatMeetingWhen(meeting.startsAtUtc, meeting.durationMinutes)}</p>
      {meeting.location && <p className="text-sm text-brand-slate-500">{meeting.location}</p>}

      {meeting.myInviteStatus && (
        <div className="mt-3">
          <Badge variant={inviteBadgeVariant[meeting.myInviteStatus]}>
            {inviteStatusLabel(meeting.myInviteStatus)}
          </Badge>
        </div>
      )}

      {rsvpError && (
        <div role="alert" className="mt-3">
          <Notice variant="error" title={rsvpError} />
        </div>
      )}

      <RsvpButtonGroup onRespond={handleRsvp} pending={responding} testIdPrefix="upcoming-meeting" />
    </Card>
  );
}
